import React, { useRef, useState } from "react";
import teamService from "../../services/api/teamService";
import { resizeImageToSquare, IMAGE_TYPES } from "../../utils/imageResize";
import { ParticipantAvatar } from "../ui/Matchup";
import "./TeamLogoUploader.css";

/**
 * The team's logo with "Upload logo" / "Change" / "Remove" for the people who
 * may change it (creator, captain, managers). The logo then shows on every
 * match card of the team.
 */
const TeamLogoUploader = ({ team, canEdit = false, onChange, size = "lg" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const teamId = team?.short_code || team?.uuid || team?.id;

  const pick = () => inputRef.current?.click();

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      const image = await resizeImageToSquare(file);
      const result = await teamService.uploadTeamLogo(teamId, image);
      onChange?.(result?.data?.logo_url ?? null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Could not upload the logo");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setError("");
    setBusy(true);
    try {
      await teamService.removeTeamLogo(teamId);
      onChange?.(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove the logo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="team-logo-uploader" data-testid="team-logo-uploader">
      <ParticipantAvatar name={team?.name} image={team?.logo_url} kind="team" size={size} />
      {canEdit && (
        <div className="team-logo-uploader__actions">
          <button type="button" className="team-logo-uploader__btn" onClick={pick} disabled={busy}>
            {busy ? "Saving…" : team?.logo_url ? "Change logo" : "Upload logo"}
          </button>
          {team?.logo_url && !busy && (
            <button type="button" className="team-logo-uploader__btn team-logo-uploader__btn--muted" onClick={remove}>
              Remove
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_TYPES.join(",")}
            onChange={upload}
            hidden
            data-testid="team-logo-input"
          />
        </div>
      )}
      {error && <p className="team-logo-uploader__error" role="alert">{error}</p>}
    </div>
  );
};

export default TeamLogoUploader;
